import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { DOT } from "@/lib/stages";
import { isUkToday, ukDay, ukTime, ukWhen } from "@/lib/time";
import { toast } from "sonner";
import { I } from "@/components/portal/sys/icons";
import { Btn, Empty, St, Tag } from "@/components/portal/crm/kit";

/**
 * CaseThread — one chronological feed per case: notes, desk messages, documents, document
 * requests, routing and every decision, in the order they happened. Replaces the floating
 * chat bubble and the separate Notes / Timeline tabs, which is how a case stops going quiet:
 * there is one place to look and one place to write.
 *
 * Masking is the server's job (`/api/hub/leads/{id}/timeline`) — this component renders
 * whatever it is given and never filters.
 */

const OK_EXT = ["pdf", "jpg", "jpeg", "png", "doc", "docx", "xls", "xlsx", "csv"];
const SAID = ["note", "message", "client_answer"];

const prettySize = (n) => {
  const v = Number(n) || 0;
  return v >= 1048576 ? `${(v / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(v / 1024))} KB`;
};

const FileChip = ({ file, testId }) => {
  const [busy, setBusy] = useState(false);
  const pull = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/hub/files/${file.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: r.data.type || file.content_type }));
      const a = document.createElement("a");
      a.href = url; a.download = file.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("That document wouldn't open — tell the desk and we'll re-send it"); }
    finally { setBusy(false); }
  };
  return (
    <button className="thread-file" onClick={pull} disabled={busy} data-testid={testId}>
      <I.doc />
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {file.filename}
      </span>
      <span style={{ color: "var(--mut)", fontWeight: 600 }}>{prettySize(file.size)}</span>
      <I.download />
    </button>
  );
};

const Receipts = ({ ev }) => {
  if (!ev.show_receipts || !ev.mine) return null;
  if (!ev.seen_by?.length) {
    return <div className="money-sub" data-testid={`thread-unseen-${ev.id}`}>Not opened yet</div>;
  }
  return (
    <div className="money-sub" data-testid={`thread-seen-${ev.id}`}>
      Seen by {ev.seen_by.map((s) => `${s.reader} · ${ukWhen(s.read_at)}`).join(" · ")}
    </div>
  );
};

export function CaseThread({ leadId, testPrefix = "thread", compact = false }) {
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState(null);
  const [posting, setPosting] = useState(false);
  const [hot, setHot] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const depth = useRef(0);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/hub/leads/${leadId}/timeline`);
      setData(r.data);
      setAudience((a) => a || r.data.audience?.default || null);
    } catch { setData({ items: [], can_post: false }); }
  }, [leadId]);

  useEffect(() => {
    let t;
    const tick = async () => { await load(); t = setTimeout(tick, 15000); };
    tick();
    return () => clearTimeout(t);
  }, [load]);

  const post = async () => {
    const text = body.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      await api.post(`/hub/leads/${leadId}/notes`, { body: text, visible_to: audience || undefined });
      setBody("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't send — try again"); }
    finally { setPosting(false); }
  };

  const [arming, setArming] = useState(null);
  const remove = async (ev) => {
    if (arming !== ev.note_id) {
      setArming(ev.note_id);
      setTimeout(() => setArming((a) => (a === ev.note_id ? null : a)), 4000);
      return;
    }
    setArming(null);
    try {
      await api.delete(`/hub/leads/${leadId}/notes/${ev.note_id}`);
      toast.success("Note removed — everyone else now sees \u201cRemoved by Connexis\u201d");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't remove — try again"); }
  };

  const upload = async (files) => {
    const list = Array.from(files || []).filter((f) => {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!OK_EXT.includes(ext)) { toast.error(`${f.name} isn't a document type we accept`); return false; }
      return true;
    });
    if (!list.length) return;
    setUploading(true);
    try {
      const ids = [];
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/hub/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push(r.data.id);
      }
      await api.post(`/hub/leads/${leadId}/attach`, { file_ids: ids });
      toast.success(`${ids.length} document${ids.length === 1 ? "" : "s"} on the case — everyone on it can see them now`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "The upload didn't finish — try again"); }
    finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    depth.current = 0; setHot(false);
    upload(e.dataTransfer?.files);
  };

  if (!data) return <Empty testId={`${testPrefix}-loading`}>Reading the thread…</Empty>;

  const items = data.items || [];
  const opts = data.audience?.options || [];

  let lastDay = null;
  const rows = [];
  items.forEach((ev) => {
    const day = ukDay(ev.at, "");
    if (day !== lastDay) {
      lastDay = day;
      rows.push(
        <div className="thread-day" key={`day-${day}-${ev.id}`}>
          <span>{isUkToday(ev.at) ? "Today" : day}</span><i />
        </div>,
      );
    }
    if (SAID.includes(ev.kind)) {
      rows.push(
        <div className={`note${ev.mine ? " mine" : ""}`} key={ev.id} data-testid={`${testPrefix}-${ev.kind}-${ev.id}`}>
          <div className="note-h">
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span className="who-s">{ev.who}</span>
              {ev.audience_label && <Tag>{ev.audience_label}</Tag>}
              {ev.kind === "message" && <Tag amber>Desk thread</Tag>}
              {ev.kind === "client_answer" && <Tag>Answered your question</Tag>}
            </div>
            <span className="date">{ukTime(ev.at, "")}</span>
          </div>
          {ev.removed ? (
            <div className="note-removed" data-testid={`${testPrefix}-removed-${ev.note_id || ev.id}`}>
              <span>Removed by Connexis</span>
              {ev.removed_body && <div className="note-removed-was" title={`Removed ${ev.removed_by ? `by ${ev.removed_by} ` : ""}${ev.removed_at ? ukWhen(ev.removed_at) : ""}`}>
                Only you can see this: &ldquo;{ev.removed_body}&rdquo;</div>}
            </div>
          ) : ev.body && <div style={{ color: "var(--ink-3)", fontSize: 13.5, whiteSpace: "pre-wrap" }}>{ev.body}</div>}
          {ev.file?.id && <FileChip file={ev.file} testId={`${testPrefix}-file-${ev.file.id}`} />}
          <Receipts ev={ev} />
          {ev.can_remove && (
            <button className={`note-remove${arming === ev.note_id ? " armed" : ""}`} onClick={() => remove(ev)}
                    data-testid={`${testPrefix}-remove-${ev.note_id}`}>
              {arming === ev.note_id ? "Tap again to remove it" : "Remove note"}
            </button>
          )}
        </div>,
      );
    } else {
      rows.push(
        <div className="tl-ev" key={ev.id} data-testid={`${testPrefix}-event-${ev.id}`}>
          <i className="sq" style={{ background: DOT[ev.tone] || DOT.slate }} />
          <div style={{ minWidth: 0 }}>
            <div className="tl-t">{ev.title}</div>
            {ev.body && <div className="cell-sub" style={{ marginTop: 3 }}>{ev.body}</div>}
            {ev.file?.id && <FileChip file={ev.file} testId={`${testPrefix}-file-${ev.file.id}`} />}
          </div>
          <span className="date">{ukTime(ev.at, "")}</span>
        </div>,
      );
    }
  });

  return (
    <div data-testid={testPrefix}
         onDragEnter={(e) => { e.preventDefault(); depth.current += 1; setHot(true); }}
         onDragOver={(e) => e.preventDefault()}
         onDragLeave={() => { depth.current = Math.max(0, depth.current - 1); if (!depth.current) setHot(false); }}
         onDrop={onDrop}>
      <div className="thread" style={compact ? { maxHeight: 420, overflowY: "auto", paddingRight: 4 } : undefined}>
        {rows.length === 0
          ? <Empty testId={`${testPrefix}-empty`}>Nothing on this case yet. Write the first line below.</Empty>
          : rows}
      </div>

      {data.can_post ? (
        <div style={{ marginTop: 18 }}>
          <textarea className="inp" value={body} onChange={(e) => setBody(e.target.value)}
                    placeholder="Write an update, ask a question, or say what you are waiting on…"
                    data-testid={`${testPrefix}-composer`} />
          <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap", marginTop: 11 }}>
            {opts.length > 1 ? (
              <select className="inp" value={audience || ""} onChange={(e) => setAudience(e.target.value)}
                      style={{ width: "auto", height: 38, paddingRight: 34 }} data-testid={`${testPrefix}-audience`}>
                {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : data.audience?.fixed_label ? (
              <St tone="slate" testId={`${testPrefix}-audience-fixed`}>{data.audience.fixed_label}</St>
            ) : null}
            <Btn kind="primary" sm onClick={post} disabled={posting || !body.trim()} testId={`${testPrefix}-send`}>
              {posting ? "Sending…" : "Post to the thread"}
            </Btn>
            <Btn sm onClick={() => fileRef.current?.click()} disabled={uploading} testId={`${testPrefix}-attach`}>
              {uploading ? "Uploading…" : "Attach a document"}
            </Btn>
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
            <span style={{ color: "var(--mut)", fontSize: 12, flex: "1 1 220px", minWidth: 0 }}
                  data-testid={`${testPrefix}-audience-note`}>
              {data.audience?.note || "It lands in their portal straight away and in their 5pm round-up email."}
            </span>
          </div>
          <div className={`thread-drop${hot ? " hot" : ""}`} style={{ marginTop: 12 }} data-testid={`${testPrefix}-dropzone`}>
            {hot ? "Drop it — it goes straight on the case" : "Or drag a document anywhere onto this thread"}
          </div>
        </div>
      ) : (
        <div className="warn" style={{ marginTop: 18 }} data-testid={`${testPrefix}-closed`}>
          <span>
            <b>This thread is closed on your side.</b> You passed on the case, so its paperwork and the live
            conversation are no longer yours. Message the Connexis desk if you want another look.
          </span>
        </div>
      )}
    </div>
  );
}

export default CaseThread;
